/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@shared/components/ui/button";
import { Separator } from "@shared/components/ui/separator";
import { ScrollArea } from "@shared/components/ui/scroll-area";
import { useToast } from "@shared/components/ui/use-toast";
import { Skeleton } from "@shared/components/ui/skeleton";
import { Badge } from "@shared/components/ui/badge";
import { CalendarIcon, ChatBubbleLeftRightIcon, UserIcon, ClockIcon } from "@heroicons/react/24/outline";
import { ChevronLeftIcon, ChevronRightIcon, GripVertical, Trash2 } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@shared/components/ui/collapsible";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@shared/components/ui/alert-dialog";
import { Checkbox } from "@shared/components/ui/checkbox";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChatSession } from "@shared/schema/chat";
import { getChatSessions, deleteChatSession } from "@shared/lib/api/chat";
import { getUserSettings, updateChatSettings } from "@shared/lib/api/user_settings";
import { ChatSettings } from "@shared/types/user_settings";
import { cn } from "@shared/lib/utils";

interface ChatHistoryPanelProps {
  onItemSelect?: (session: ChatSession) => void;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export function ChatHistoryPanel({ 
  onItemSelect, 
  isExpanded: controlledIsExpanded,
  onExpandedChange 
}: ChatHistoryPanelProps) {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  const [panelWidth, setPanelWidth] = useState<number>(640);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<ChatSession | null>(null);
  const [skipConfirmation, setSkipConfirmation] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const resizeRef = useRef<HTMLDivElement>(null);

  // Use controlled state if provided, otherwise use internal state
  const isExpanded = controlledIsExpanded !== undefined ? controlledIsExpanded : internalIsExpanded;
  const setIsExpanded = onExpandedChange || setInternalIsExpanded;

  // Fetch user settings for delete confirmation preference
  const { data: userSettings } = useQuery({
    ...getUserSettings,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (sessionId: number) => deleteChatSession(sessionId),
    onSuccess: (data, sessionId) => {
      toast({
        title: "Session deleted",
        description: "Chat session has been successfully deleted",
      });
      // Remove the session from the local state
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      // Invalidate and refetch sessions
      queryClient.invalidateQueries({ queryKey: ['/api/chat/sessions'] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete chat session. Please try again.",
        variant: "destructive"
      });
      console.error('Failed to delete session:', error);
    },
  });

  // Resize handlers (adapted for right-side panel)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !resizeRef.current) return;
    
    // Get the panel container (the collapsible content)
    const panelElement = resizeRef.current.closest('[data-state]'); // CollapsibleContent
    if (!panelElement) return;
    
    const panelRect = panelElement.getBoundingClientRect();
    
    // For right-side panel, calculate width based on distance from mouse to right edge
    const newWidth = panelRect.right - e.clientX;
    
    if (newWidth >= 280 && newWidth <= 800) {
      setPanelWidth(newWidth);
    }
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const fetchSessions = async () => {
    setIsLoading(true);
    try {
      // Fetch recent chat sessions, sorted by last activity
      const data = await getChatSessions({
        // Don't filter by isActive for now to show all sessions in testing
        limit: 50,
        offset: 0,
        sortBy: 'lastActivityAt',
        sortOrder: 'desc'
      });
      setSessions(data);
    } catch (error) {
      console.error("Error fetching chat sessions:", error);
      toast({
        title: "Error",
        description: "Failed to fetch chat session history",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isExpanded) {
      fetchSessions();
    }
  }, [isExpanded]);

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return 'Unknown date';
    // Ensure we have a Date object
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(dateObj);
  };

  const handleItemClick = (session: ChatSession) => {
    if (onItemSelect) {
      onItemSelect(session);
      // Keep the panel open for browsing multiple history items
    }
  };

  const handleDeleteClick = (session: ChatSession, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering item click
    
    // Check if confirmation is disabled in settings
    if (userSettings?.chatSettings?.deleteConfirmation === false) {
      // Delete immediately without confirmation
      deleteMutation.mutate(session.id);
    } else {
      // Show confirmation dialog
      setSessionToDelete(session);
      setDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (sessionToDelete) {
      deleteMutation.mutate(sessionToDelete.id);
      
      // If user chose to skip future confirmations, update the preference
      if (skipConfirmation) {
        try {
          // Update the chat settings to disable confirmations
          await updateChatSettings({ deleteConfirmation: false });
          
          // Invalidate the query cache to immediately reflect the change
          queryClient.invalidateQueries({ queryKey: ['/api/settings/users'] });
        } catch (error) {
          console.error('Error updating chat preferences:', error);
        }
      }
    }
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
    setSkipConfirmation(false);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
    setSkipConfirmation(false);
  };

  const getSessionBadge = (session: ChatSession) => {
    if (!session.isActive) {
      return <Badge variant="outline" className="h-5 px-2">Inactive</Badge>;
    }
    
    if ((session.messageCount ?? 0) === 0) {
      return <Badge variant="secondary" className="h-5 px-2">New</Badge>;
    }

    return (
      <Badge variant="default" className="h-5 px-2 bg-blue-100 text-blue-800 border-blue-200">
        {session.messageCount} message{session.messageCount !== 1 ? 's' : ''}
      </Badge>
    );
  };

  const getSessionTitle = (session: ChatSession) => {
    if (session.title && session.title.trim()) {
      return session.title;
    }
    return `Chat Session ${session.sessionKey.split('-')[2] || session.id}`;
  };

  const getSessionDescription = (session: ChatSession) => {
    if (session.description && session.description.trim()) {
      return session.description;
    }
    
    if ((session.messageCount ?? 0) > 0) {
      return `${session.messageCount} message${session.messageCount !== 1 ? 's' : ''} • ${session.agentCount ?? 0} agent${(session.agentCount ?? 0) !== 1 ? 's' : ''}`;
    }
    
    return 'No messages yet';
  };

  return (
    <div className="h-full relative flex items-start" data-chat-history-panel>
      {/* Toggle Button - Always visible */}
      <Collapsible
        open={isExpanded}
        onOpenChange={setIsExpanded}
        className="h-full flex items-start"
      >
        <CollapsibleTrigger asChild>
          <Button data-agent-chat-expand-button
            variant="outline" 
            size="icon" 
            className="h-8 w-8 border border-slate-200 bg-white shadow-sm hover:bg-slate-100 dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700 flex-shrink-0"
          >
            {isExpanded ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronLeftIcon className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
        
        <CollapsibleContent 
          className={cn(
            "transition-all duration-300 h-full overflow-hidden relative border-l bg-background",
            !isExpanded && "w-0 border-0"
          )}
          style={{ width: isExpanded ? `${panelWidth}px` : '0px' }}
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-slate-200 dark:border-slate-700">
              <h3 className="font-medium flex items-center gap-2">
                <ChatBubbleLeftRightIcon className="h-4 w-4" />
                Chat History
              </h3>
              {sessions.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {sessions.length}
                </Badge>
              )}
            </div>
            
            {/* Sessions Content */}
            <ScrollArea className="flex-1 p-3">
              <div className="space-y-4">
                {isLoading && (
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="space-y-2">
                        <Skeleton className="h-6 w-2/3" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-1/2" />
                      </div>
                    ))}
                  </div>
                )}
                
                {!isLoading && sessions.length > 0 && (
                  <div className="space-y-2">
                    {sessions.map((session) => (
                      <div 
                        key={session.id} 
                        className="p-3 border rounded-md hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                        onClick={() => handleItemClick(session)}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <ChatBubbleLeftRightIcon className="h-4 w-4 text-slate-500 flex-shrink-0" />
                              <span className="font-medium truncate">
                                {getSessionTitle(session)}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground truncate">
                              {getSessionDescription(session)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {getSessionBadge(session)}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                              onClick={(e) => handleDeleteClick(session, e)}
                              disabled={deleteMutation.isPending}
                              title="Delete session"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Agent count and activity */}
                        {(session.agentCount ?? 0) > 0 && (
                          <div className="flex items-center gap-3 mb-2 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <UserIcon className="h-3 w-3" />
                              <span>{session.agentCount} agent{(session.agentCount ?? 0) !== 1 ? 's' : ''}</span>
                            </div>
                            {(session.toolCallsCount ?? 0) > 0 && (
                              <div className="flex items-center gap-1">
                                <span>⚡</span>
                                <span>{session.toolCallsCount} tool call{(session.toolCallsCount ?? 0) !== 1 ? 's' : ''}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Token usage if available */}
                        {(session.totalTokensUsed ?? 0) > 0 && (
                          <div className="text-xs text-muted-foreground mb-2">
                            <span className="font-medium">Tokens:</span> {session.totalTokensUsed?.toLocaleString()}
                          </div>
                        )}
                        
                        <div className="text-xs text-muted-foreground flex items-center justify-between">
                          <div className="flex items-center">
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {formatDate(session.lastActivityAt)}
                          </div>
                          <div className="text-xs opacity-75">
                            ID: {session.sessionKey.substring(0, 8)}...
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {!isLoading && sessions.length === 0 && (
                  <div className="text-center py-12">
                    <ChatBubbleLeftRightIcon className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm">No chat sessions found</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Start a conversation to see your chat history here
                    </p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
          
          {/* Resize Handle */}
          {isExpanded && (
            <div
              ref={resizeRef}
              className="absolute top-0 left-0 w-1 h-full cursor-col-resize bg-border/50 hover:bg-border transition-colors opacity-0 hover:opacity-100"
              onMouseDown={handleMouseDown}
              style={{ 
                background: isResizing ? 'rgb(var(--border))' : undefined,
                opacity: isResizing ? 1 : undefined 
              }}
            >
              <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Chat Session</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this chat session? This action cannot be undone.
              {sessionToDelete && (
                <div className="mt-2 p-2 bg-slate-100 dark:bg-slate-800 rounded text-sm">
                  <strong>{getSessionTitle(sessionToDelete)}</strong>
                  <br />
                  {sessionToDelete.messageCount} message{sessionToDelete.messageCount !== 1 ? 's' : ''}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="skip-confirmation" 
                checked={skipConfirmation}
                onCheckedChange={(checked) => setSkipConfirmation(checked === true)}
              />
              <label 
                htmlFor="skip-confirmation" 
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Don't ask for confirmation again
              </label>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
